import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { Card } from "@/components/ui/card";
import { PdfDocumentViewer } from "@/section/pdf";
import type { PdfSource } from "@/section/pdf";
import { useGetDestinationPacketInstanceDocument } from "@/lib/api/destinationPackets";

type DocumentPreviewType = "sent_document_url" | "final_document_url";

interface PreviewConfig {
  title: string;
  description: string;
  documentKey: DocumentPreviewType;
}

function BaseDestinationPacketsInstancePreviewPage({
  title,
  description,
  documentKey,
}: PreviewConfig) {
  const { template_id, instance_id } = useParams<{ template_id: string; instance_id: string }>();
  const getInstanceDocument = useGetDestinationPacketInstanceDocument();

  const [pdfSource, setPdfSource] = useState<PdfSource | string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const documentFetcherRef = useRef(getInstanceDocument);

  useEffect(() => {
    documentFetcherRef.current = getInstanceDocument;
  }, [getInstanceDocument]);

  useEffect(() => {
    if (!template_id || !instance_id) {
      setError("Missing destination packet template or instance id.");
      setPdfSource(null);
      return;
    }

    let isMounted = true;
    setIsLoading(true);
    setError(null);
    setPdfSource(null);

    (async () => {
      try {
        const result = await documentFetcherRef.current(template_id, instance_id);
        if (!isMounted) return;

        if (!result.success) {
          throw new Error("Failed to retrieve document metadata.");
        }

        const url = result.data?.[documentKey];
        if (!url) {
          throw new Error("Document is not available for preview.");
        }

        setPdfSource(url);
      } catch (err: unknown) {
        if (!isMounted) return;
        const description =
          err instanceof Error ? err.message : "Unable to load document preview.";
        setError(description);
        setPdfSource(null);
      } finally {
        if (!isMounted) return;
        setIsLoading(false);
      }
    })();

    return () => {
      isMounted = false;
    };
  }, [template_id, instance_id, documentKey]);

  const showViewer = !!pdfSource && !isLoading && !error;

  return (
    <div className="flex h-full flex-col gap-4">
      <div>
        <p className="text-sm uppercase text-muted-foreground tracking-wide">
          Destination Packets
        </p>
        <h2 className="text-2xl font-bold">{title}</h2>
      </div>

      {description && <p className="text-sm text-muted-foreground">{description}</p>}

      {!showViewer && (
        <Card className="flex flex-1 items-center justify-center p-8">
          {isLoading ? (
            <div className="flex flex-col items-center gap-2 text-muted-foreground">
              <Loader2 className="h-5 w-5 animate-spin" />
              <span>Loading document…</span>
            </div>
          ) : (
            <div className="text-sm text-muted-foreground text-center">
              {error ?? "Select a destination packet document to view the preview."}
            </div>
          )}
        </Card>
      )}

      {showViewer && (
        <Card className="flex-1 overflow-hidden">
          <PdfDocumentViewer source={pdfSource} width={720} />
        </Card>
      )}
    </div>
  );
}

export function DestinationPacketsInstanceSentPreviewPage() {
  return (
    <BaseDestinationPacketsInstancePreviewPage
      title="Sent Document Preview"
      description="This is the PDF that was originally sent to the signing service."
      documentKey="sent_document_url"
    />
  );
}

export function DestinationPacketsInstanceFinalPreviewPage() {
  return (
    <BaseDestinationPacketsInstancePreviewPage
      title="Final Document Preview"
      description="This is the final PDF returned by the signing service."
      documentKey="final_document_url"
    />
  );
}

